"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import {
  PersonalDetailsForm,
  type PersonalDetailsValues,
  type PersonalDetailsErrors,
} from "@baluu/ui/components/checkout/personal-details-form";
import { CheckoutSteps } from "@baluu/ui/components/checkout/checkout-steps";
import { Checkbox } from "@baluu/ui/components/checkbox";
import { Label } from "@baluu/ui/components/label";
import { Input } from "@baluu/ui/components/input";
import { PhoneCountryInput } from "@baluu/ui/components/phone-country-input";
import { InlineMarkdown } from "@baluu/ui/components/inline-markdown";

// Guest data structure for multi-guest forms
type GuestData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  marketingOptIn?: boolean;
  mandatoryTickBox?: boolean;
};

type DeliveryAddress = {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postcode: string;
  country: string;
};

type PersonalDetailsStepProps = {
  // Single guest mode (backward compatible)
  values?: PersonalDetailsValues;
  errors?: PersonalDetailsErrors;
  touched?: Partial<Record<keyof PersonalDetailsValues, boolean>>;
  onChange?: (
    field: keyof PersonalDetailsValues,
    value: string | boolean,
  ) => void;
  onBlur?: (field: keyof PersonalDetailsValues) => void;

  // Multi-guest mode
  guests?: GuestData[];
  guestErrors?: Array<Partial<Record<keyof GuestData, string>>>;
  guestTouched?: Array<Partial<Record<keyof GuestData, boolean>>>;
  onGuestChange?: (
    guestIndex: number,
    field: keyof GuestData,
    value: string | boolean,
  ) => void;
  onGuestBlur?: (guestIndex: number, field: keyof GuestData) => void;
  getGuestLabel?: (
    index: number,
  ) => { label?: string; description?: string } | undefined;

  // Feature flags
  disabled?: boolean;
  showPhone?: boolean;
  showMarketingOptIn?: boolean;
  businessName?: string;
  /** Custom marketing opt-in label (markdown supported). Overrides the default label. */
  customMarketingOptInLabel?: string | null;
  className?: string;

  // Multi-guest specific options
  allowPrefillOthersGuestsOnCheckout?: boolean;

  // Terms and conditions (for first guest)
  mandatoryTickBox?: boolean;
  termsUrl?: string;

  // Delivery address
  isSendingMaterial?: boolean;
  deliveryAddress?: DeliveryAddress;
  deliveryAddressErrors?: Partial<Record<keyof DeliveryAddress, string>>;
  deliveryAddressTouched?: Partial<Record<keyof DeliveryAddress, boolean>>;
  onDeliveryAddressChange?: (
    field: keyof DeliveryAddress,
    value: string,
  ) => void;
  onDeliveryAddressBlur?: (field: keyof DeliveryAddress) => void;

  // School-specific customization
  schoolID?: string;
  businessCountry?: string;
};

// Placeholder data for prefilled guests
const PREFILL_GUEST_DATA = {
  email: "no-reply@baluu.co.uk",
  phoneNumber: "123456789",
};

function PersonalDetailsStep({
  // Single guest props
  values,
  errors,
  touched,
  onChange,
  onBlur,

  // Multi-guest props
  guests,
  guestErrors,
  guestTouched,
  onGuestChange,
  onGuestBlur,
  getGuestLabel,

  // Options
  disabled = false,
  showPhone = true,
  showMarketingOptIn = true,
  businessName,
  customMarketingOptInLabel,
  className,

  // Multi-guest specific
  allowPrefillOthersGuestsOnCheckout = false,

  // Terms
  mandatoryTickBox = false,
  termsUrl,

  // Delivery
  isSendingMaterial = false,
  deliveryAddress,
  deliveryAddressErrors = {},
  deliveryAddressTouched = {},
  onDeliveryAddressChange,
  onDeliveryAddressBlur,

  schoolID,
  businessCountry,
}: PersonalDetailsStepProps) {
  // Resolve default country code: specific school override > business country > UK fallback
  const resolvedDefaultCountryCode =
    schoolID === "bmDxPjPqe7QoNLk76" || schoolID === "3t7gZeu8pz7AiAeAM"
      ? "IL"
      : businessCountry || "GB";
  // State for prefill toggle
  const [prefillOthers, setPrefillOthers] = React.useState(
    allowPrefillOthersGuestsOnCheckout,
  );

  // Effect to prefill other guests when toggle is enabled
  React.useEffect(() => {
    if (!prefillOthers || !guests || guests.length <= 1 || !onGuestChange)
      return;

    // Prefill all guests except the first one
    guests.forEach((guest, index) => {
      if (index === 0) return;

      // Set prefilled values for other guests
      onGuestChange(index, "firstName", `guest${index}_firstname`);
      onGuestChange(index, "lastName", `guest${index}_lastname`);
      onGuestChange(index, "email", PREFILL_GUEST_DATA.email);
      onGuestChange(index, "phoneNumber", PREFILL_GUEST_DATA.phoneNumber);
    });
  }, [prefillOthers, guests?.length]);

  const marketingLabel = customMarketingOptInLabel
    ? customMarketingOptInLabel
    : businessName
      ? `I'd like to receive updates and offers from ${businessName}`
      : "I'd like to receive updates and offers via email";

  const defaultTermsUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/terms`
      : "/terms";
  const actualTermsUrl = termsUrl || defaultTermsUrl;

  // Determine if we're in multi-guest mode
  const isMultiGuestMode = guests && guests.length > 0 && onGuestChange;

  // Render single guest form (backward compatible)
  const renderSingleGuestForm = () => {
    if (!values || !onChange) return null;

    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="font-medium text-foreground mb-4">Your Details</h3>
        <PersonalDetailsForm
          values={values}
          errors={errors}
          touched={touched}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          showPhone={showPhone}
          showMarketingOptIn={false} // We render this manually to control order
          marketingOptInLabel={marketingLabel}
          defaultCountryCode={resolvedDefaultCountryCode}
        />

        {/* Terms checkbox - BEFORE marketing opt-in for better visibility */}
        {mandatoryTickBox && (
          <div className="mt-5 p-4 bg-muted rounded-lg border-2 border-border">
            <div className="flex items-start gap-3">
              <Checkbox
                id="mandatoryTickBox"
                checked={!!values.mandatoryTickBox}
                onCheckedChange={(checked) =>
                  onChange(
                    "mandatoryTickBox" as keyof PersonalDetailsValues,
                    checked === true,
                  )
                }
                disabled={disabled}
                className="h-5 w-5 mt-0.5 shrink-0 border-2"
              />
              <Label
                htmlFor="mandatoryTickBox"
                className="block text-sm sm:text-base text-foreground font-normal cursor-pointer leading-relaxed"
              >
                I agree to the{" "}
                <a
                  href={actualTermsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80"
                  onClick={(e) => e.stopPropagation()}
                >
                  terms and conditions
                </a>{" "}
                for this purchase.{" "}
                <span className="text-destructive">*</span>
              </Label>
            </div>
            {!values.mandatoryTickBox && touched?.mandatoryTickBox && (
              <p className="text-sm text-destructive mt-2 ml-8">
                Please accept the terms and conditions to continue
              </p>
            )}
          </div>
        )}

        {/* Marketing opt-in - after terms */}
        {showMarketingOptIn && (
          <div className="flex items-start gap-3 mt-4 pt-4 border-t border-border-subtle">
            <Checkbox
              id="marketingOptIn"
              checked={values.marketingOptIn}
              onCheckedChange={(checked) =>
                onChange("marketingOptIn", checked === true)
              }
              disabled={disabled}
            />
            <Label
              htmlFor="marketingOptIn"
              className="text-sm text-foreground-muted font-normal cursor-pointer"
            >
              <InlineMarkdown multiline>{marketingLabel}</InlineMarkdown>
            </Label>
          </div>
        )}
      </div>
    );
  };

  // Render multi-guest forms
  const renderMultiGuestForms = () => {
    if (!guests || !onGuestChange) return null;

    return (
      <div className="space-y-4">
        {guests.map((guest, index) => {
          // Skip rendering if prefill is enabled and not the first guest
          if (prefillOthers && index > 0) return null;

          const labelInfo = getGuestLabel?.(index) || {};
          const label =
            labelInfo.label ||
            (index === 0 ? "Your Details" : `Guest ${index + 1}`);
          const description = labelInfo.description;

          const guestError = guestErrors?.[index] || {};
          const guestTouch = guestTouched?.[index] || {};

          return (
            <div
              key={index}
              className="bg-card rounded-lg border border-border p-6"
            >
              <div className="mb-4">
                <h3 className="font-medium text-foreground">{label}</h3>
                {description && (
                  <p className="text-sm text-foreground-muted mt-1">
                    {description}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                {/* Name Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`guest-${index}-firstName`}>
                      First name *
                    </Label>
                    <Input
                      id={`guest-${index}-firstName`}
                      type="text"
                      value={guest.firstName}
                      onChange={(e) =>
                        onGuestChange(index, "firstName", e.target.value)
                      }
                      onBlur={() => onGuestBlur?.(index, "firstName")}
                      disabled={disabled}
                      placeholder="Enter first name"
                      className={cn(
                        guestTouch.firstName &&
                          guestError.firstName &&
                          "border-destructive",
                      )}
                    />
                    {guestTouch.firstName && guestError.firstName && (
                      <p className="text-sm text-destructive">
                        {guestError.firstName}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`guest-${index}-lastName`}>
                      Last name *
                    </Label>
                    <Input
                      id={`guest-${index}-lastName`}
                      type="text"
                      value={guest.lastName}
                      onChange={(e) =>
                        onGuestChange(index, "lastName", e.target.value)
                      }
                      onBlur={() => onGuestBlur?.(index, "lastName")}
                      disabled={disabled}
                      placeholder="Enter last name"
                      className={cn(
                        guestTouch.lastName &&
                          guestError.lastName &&
                          "border-destructive",
                      )}
                    />
                    {guestTouch.lastName && guestError.lastName && (
                      <p className="text-sm text-destructive">
                        {guestError.lastName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email - required for first guest, optional for others */}
                <div className="space-y-2">
                  <Label htmlFor={`guest-${index}-email`}>
                    Email{index === 0 ? " *" : ""}
                  </Label>
                  <Input
                    id={`guest-${index}-email`}
                    type="email"
                    value={guest.email}
                    onChange={(e) =>
                      onGuestChange(index, "email", e.target.value)
                    }
                    onBlur={() => onGuestBlur?.(index, "email")}
                    disabled={disabled}
                    placeholder="Enter email address"
                    className={cn(
                      guestTouch.email &&
                        guestError.email &&
                        "border-destructive",
                    )}
                  />
                  {guestTouch.email && guestError.email && (
                    <p className="text-sm text-destructive">
                      {guestError.email}
                    </p>
                  )}
                </div>

                {/* Phone with country code - only for first guest */}
                {index === 0 && showPhone && (
                  <div className="space-y-2">
                    <Label htmlFor={`guest-${index}-phoneNumber`}>
                      Phone number *
                    </Label>
                    <PhoneCountryInput
                      value={guest.phoneNumber}
                      onChange={(fullPhone) =>
                        onGuestChange(index, "phoneNumber", fullPhone)
                      }
                      onBlur={() => onGuestBlur?.(index, "phoneNumber")}
                      disabled={disabled}
                      hasError={
                        !!(guestTouch.phoneNumber && guestError.phoneNumber)
                      }
                      defaultCountryCode={resolvedDefaultCountryCode}
                      container={null}
                    />
                    {guestTouch.phoneNumber && guestError.phoneNumber && (
                      <p className="text-sm text-destructive">
                        {guestError.phoneNumber}
                      </p>
                    )}
                  </div>
                )}

                {/* Terms checkbox - BEFORE marketing opt-in, only for first guest */}
                {index === 0 && mandatoryTickBox && (
                  <div className="mt-3 p-4 bg-muted rounded-lg border-2 border-border">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={`guest-${index}-mandatoryTickBox`}
                        checked={!!guest.mandatoryTickBox}
                        onCheckedChange={(checked) =>
                          onGuestChange(
                            index,
                            "mandatoryTickBox",
                            checked === true,
                          )
                        }
                        disabled={disabled}
                        className="h-5 w-5 mt-0.5 shrink-0 border-2"
                      />
                      <Label
                        htmlFor={`guest-${index}-mandatoryTickBox`}
                        className="block text-sm sm:text-base text-foreground font-normal cursor-pointer leading-relaxed"
                      >
                        I agree to the{" "}
                        <a
                          href={actualTermsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80"
                          onClick={(e) => e.stopPropagation()}
                        >
                          terms and conditions
                        </a>{" "}
                        for this purchase.{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                    </div>
                    {!guest.mandatoryTickBox && guestTouch.mandatoryTickBox && (
                      <p className="text-sm text-destructive mt-2 ml-8">
                        Please accept the terms and conditions to continue
                      </p>
                    )}
                  </div>
                )}

                {/* Marketing opt-in - AFTER terms, only for first guest */}
                {index === 0 && showMarketingOptIn && (
                  <div className="flex items-start gap-3 pt-4 mt-2">
                    <Checkbox
                      id={`guest-${index}-marketingOptIn`}
                      checked={!!guest.marketingOptIn}
                      onCheckedChange={(checked) =>
                        onGuestChange(index, "marketingOptIn", checked === true)
                      }
                      disabled={disabled}
                    />
                    <Label
                      htmlFor={`guest-${index}-marketingOptIn`}
                      className="text-sm text-foreground-muted font-normal cursor-pointer"
                    >
                      <InlineMarkdown multiline>{marketingLabel}</InlineMarkdown>
                    </Label>
                  </div>
                )}
              </div>

              {/* Prefill toggle - only show for first guest when enabled and multiple guests */}
              {allowPrefillOthersGuestsOnCheckout &&
                guests.length > 1 &&
                index === 0 && (
                  <div className="mt-4 pt-4 border-t border-border-subtle">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="prefillOthers"
                        checked={prefillOthers}
                        onCheckedChange={(checked) =>
                          setPrefillOthers(checked === true)
                        }
                        disabled={disabled}
                      />
                      <Label
                        htmlFor="prefillOthers"
                        className="text-sm text-foreground-muted font-normal cursor-pointer"
                      >
                        I prefer not to share information about the other guests
                        at this time.
                      </Label>
                    </div>
                  </div>
                )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render delivery address form
  const renderDeliveryAddress = () => {
    if (!isSendingMaterial || !deliveryAddress || !onDeliveryAddressChange)
      return null;

    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-4">
          <h3 className="font-medium text-foreground">Delivery Address</h3>
          <p className="text-sm text-foreground-muted mt-1">
            The delivery charge is included within the price. Please enter the
            address you would like your materials sent to. They will arrive
            within 5 days of your class start date.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="addressLine1">Address Line 1 *</Label>
            <Input
              id="addressLine1"
              type="text"
              value={deliveryAddress.addressLine1}
              onChange={(e) =>
                onDeliveryAddressChange("addressLine1", e.target.value)
              }
              onBlur={() => onDeliveryAddressBlur?.("addressLine1")}
              disabled={disabled}
              placeholder="Enter street address"
              className={cn(
                deliveryAddressTouched.addressLine1 &&
                  deliveryAddressErrors.addressLine1 &&
                  "border-destructive",
              )}
            />
            {deliveryAddressTouched.addressLine1 &&
              deliveryAddressErrors.addressLine1 && (
                <p className="text-sm text-destructive">
                  {deliveryAddressErrors.addressLine1}
                </p>
              )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="addressLine2">Address Line 2</Label>
            <Input
              id="addressLine2"
              type="text"
              value={deliveryAddress.addressLine2 || ""}
              onChange={(e) =>
                onDeliveryAddressChange("addressLine2", e.target.value)
              }
              onBlur={() => onDeliveryAddressBlur?.("addressLine2")}
              disabled={disabled}
              placeholder="Apartment, suite, etc. (optional)"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                type="text"
                value={deliveryAddress.city}
                onChange={(e) =>
                  onDeliveryAddressChange("city", e.target.value)
                }
                onBlur={() => onDeliveryAddressBlur?.("city")}
                disabled={disabled}
                placeholder="Enter city"
                className={cn(
                  deliveryAddressTouched.city &&
                    deliveryAddressErrors.city &&
                    "border-destructive",
                )}
              />
              {deliveryAddressTouched.city && deliveryAddressErrors.city && (
                <p className="text-sm text-destructive">
                  {deliveryAddressErrors.city}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="postcode">Postcode *</Label>
              <Input
                id="postcode"
                type="text"
                value={deliveryAddress.postcode}
                onChange={(e) =>
                  onDeliveryAddressChange("postcode", e.target.value)
                }
                onBlur={() => onDeliveryAddressBlur?.("postcode")}
                disabled={disabled}
                placeholder="Enter postcode"
                className={cn(
                  deliveryAddressTouched.postcode &&
                    deliveryAddressErrors.postcode &&
                    "border-destructive",
                )}
              />
              {deliveryAddressTouched.postcode &&
                deliveryAddressErrors.postcode && (
                  <p className="text-sm text-destructive">
                    {deliveryAddressErrors.postcode}
                  </p>
                )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">Country *</Label>
            <Input
              id="country"
              type="text"
              value={deliveryAddress.country}
              onChange={(e) =>
                onDeliveryAddressChange("country", e.target.value)
              }
              onBlur={() => onDeliveryAddressBlur?.("country")}
              disabled={disabled}
              placeholder="Enter country"
              className={cn(
                deliveryAddressTouched.country &&
                  deliveryAddressErrors.country &&
                  "border-destructive",
              )}
            />
            {deliveryAddressTouched.country &&
              deliveryAddressErrors.country && (
                <p className="text-sm text-destructive">
                  {deliveryAddressErrors.country}
                </p>
              )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cn("space-y-6", className)}>
      <CheckoutSteps.Header
        title="Personal Details"
        description="Please provide your contact information to complete your booking."
      />

      {isMultiGuestMode ? renderMultiGuestForms() : renderSingleGuestForm()}

      {renderDeliveryAddress()}
    </div>
  );
}

export { PersonalDetailsStep };
export type { PersonalDetailsStepProps, GuestData, DeliveryAddress };
